import { Injectable } from '@angular/core';
import { doc, getDoc, setDoc, updateDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { FirebaseService } from './firebase.service';

@Injectable({
  providedIn: 'root'
})
export class UserFlagsService {
  private flagsSubject = new BehaviorSubject<Record<string, any>>({});
  public readonly flags$: Observable<Record<string, any>> = this.flagsSubject.asObservable().pipe(
    distinctUntilChanged(),
    shareReplay(1)
  );

  private flagsUnsubscribe?: () => void;

  constructor(
    private authService: AuthService,
    private firebaseService: FirebaseService
  ) {
    this.authService.user$.subscribe(user => {
      if (user) {
        this.listenToUserFlags(user.uid);
      } else {
        this.stopListeningToUserFlags(); // cleanup
        this.flagsSubject.next({});
      }
    });
  }

  /** Real-time listener for user flags */
  private listenToUserFlags(uid: string) {
    const docRef = doc(this.firebaseService.firestore, 'users', uid, 'flags', 'flagsDoc');

    // save unsubscribe reference
    this.flagsUnsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          this.flagsSubject.next(snapshot.data() || {});
        } else {
          this.flagsSubject.next({});
        }
      },
      (error) => {
        console.error('Error listening to user flags:', error);
        this.flagsSubject.next({});
      }
    );
  }

  /** Stop listening (awaitable) */
  public async stopListeningToUserFlags(): Promise<void> {
    if (this.flagsUnsubscribe) {
      this.flagsUnsubscribe();
      this.flagsUnsubscribe = undefined;
    }
    this.flagsSubject.next({});
  }

  /** Get all current flags (sync) */
  getCurrentFlags(): Record<string, any> {
    return this.flagsSubject.getValue();
  }

  /** Check if a specific flag is set */
  hasFlag(flagName: string): boolean {
    return this.getCurrentFlags().hasOwnProperty(flagName);
  }

  /** Set a new flag with timestamp */
  async setFlag(flagName: string): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) throw new Error('No authenticated user');

    const docRef = doc(this.firebaseService.firestore, 'users', user.uid, 'flags', 'flagsDoc');
    await setDoc(docRef, {
      [flagName]: serverTimestamp()
    }, { merge: true });
  }

  /** Manually fetch all flags once (no realtime) */
  async fetchAllFlags(): Promise<Record<string, any>> {
    const user = this.authService.getCurrentUser();
    if (!user) throw new Error('No authenticated user');

    const docRef = doc(this.firebaseService.firestore, 'users', user.uid, 'flags', 'flagsDoc');
    const snap = await getDoc(docRef);
    return snap.exists() ? snap.data() || {} : {};
  }

  /** Remove a flag */
  async removeFlag(flagName: string): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) throw new Error('No authenticated user');

    const docRef = doc(this.firebaseService.firestore, 'users', user.uid, 'flags', 'flagsDoc');
    await updateDoc(docRef, {
      [flagName]: null // This deletes the key but keeps the doc
    });
  }
}

